import React, { useState, useEffect, useMemo } from "react";
import { Server, Radio, ArrowUp, ArrowDown, Loader2, ArrowUpDown } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";

interface TcpConnection {
    state: string;
    local_port: number;
    remote_port: number;
    local_address: string;
    remote_address: string;
}

interface UdpListener {
    local_port: number;
    local_address: string;
}

interface NetworkConnectionsProps {
    asset_id: string | number;
    defaultItemsPerPage?: number;
    client_id?: string | number;
}

const NetworkConnections: React.FC<NetworkConnectionsProps> = ({ asset_id, defaultItemsPerPage = 10, client_id }) => {
    const [activeTab, setActiveTab] = useState<"tcp" | "udp">("tcp");
    const [loading, setLoading] = useState(true);

    // Raw strings from API
    const [rawTcp, setRawTcp] = useState<string | null>(null);
    const [rawUdp, setRawUdp] = useState<string | null>(null);

    // Client-side pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    // Reset page to 1 when switching tabs
    useEffect(() => {
        setCurrentPage(1);
        setSortConfig(null); // Reset sort when changing tabs
    }, [activeTab]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ asset_id: parseInt(asset_id as string), client_id: client_id }),
            };
            const response = await baseapi.get("/switch_details", { params });
            if (response.data.status) {
                const fetchedDetails = response.data.data?.[0] || response.data.data;
                if (fetchedDetails) {
                    setRawTcp(fetchedDetails.tcp_connections || null);
                    setRawUdp(fetchedDetails.udp_listeners || null);
                }
            }
        } catch (error) {
            console.error("Error fetching network connections", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (asset_id) fetchData();
    }, [asset_id]);

    // Parse and memoize raw arrays
    const tcpData: TcpConnection[] = useMemo(() => {
        if (!rawTcp) return [];
        try {
            return typeof rawTcp === "string" ? JSON.parse(rawTcp) : rawTcp;
        } catch {
            return [];
        }
    }, [rawTcp]);

    const udpData: UdpListener[] = useMemo(() => {
        if (!rawUdp) return [];
        try {
            return typeof rawUdp === "string" ? JSON.parse(rawUdp) : rawUdp;
        } catch {
            return [];
        }
    }, [rawUdp]);

    // Determine the active dataset based on the current tab
    const activeData = activeTab === "tcp" ? tcpData : udpData;
    const totalItems = activeData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

    const handleSort = (key: string) => {
        let newDirection: "asc" | "desc" = "asc";
        if (sortConfig && sortConfig.key === key) {
            newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
        }
        setSortConfig({ key, direction: newDirection });
        setCurrentPage(1);
    };

    const sortedData = useMemo(() => {
        if (!sortConfig) return activeData;
        const { key, direction } = sortConfig;

        return [...activeData].sort((a: any, b: any) => {
            let aValue = a[key];
            let bValue = b[key];

            if (aValue === undefined || aValue === null) aValue = '';
            if (bValue === undefined || bValue === null) bValue = '';

            if (key === 'local_port' || key === 'remote_port') {
                return direction === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
            }

            aValue = aValue.toString().toLowerCase();
            bValue = bValue.toString().toLowerCase();

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [activeData, sortConfig]);

    // Slice data for client-side pagination
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedData.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedData, currentPage, itemsPerPage]);

    const SortableHeader = ({ label, sortKey, className = "" }: { label: string; sortKey: string; className?: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className={`px-4 py-2 text-[9px] font-black text-left text-slate-500 dark:text-slate-100 uppercase tracking-widest cursor-pointer group select-none ${className}`}
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ArrowUp size={10} className="text-orange-500" />
                            ) : (
                                <ArrowDown size={10} className="text-orange-500" />
                            )
                        ) : (
                            <ArrowDown size={10} className="text-slate-400 dark:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    if (loading) {
        return (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 flex flex-col items-center justify-center min-h-[200px] shadow-sm">
                <Loader2 className="w-6 h-6 text-orange-500 animate-spin mb-3" />
                <p className="text-slate-500 dark:text-slate-400 text-xs font-medium uppercase tracking-widest">Loading connections...</p>
            </div>
        );
    }

    return (
        <div className="space-y-3 animate-fade-in">
            {/* Header & Tabs Container */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className="flex items-center gap-2">
                    <div className="bg-orange-600 p-1.5 rounded-lg shadow-lg shadow-orange-900/20">
                        <ArrowUpDown className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white uppercase tracking-tight">Network Connections</h2>
                </div>

                {/* Inner Tabs Toggle */}
                <div className="flex bg-slate-100 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-700/50 rounded-lg p-1">
                    <button
                        onClick={() => setActiveTab("tcp")}
                        className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "tcp"
                            ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm"
                            : "text-slate-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-800/50"
                            }`}
                    >
                        <Server className="w-3.5 h-3.5" />
                        TCP <span className="ml-1 px-1 py-0.5 rounded-md bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[8px] leading-none">{tcpData.length}</span>
                    </button>
                    <button
                        onClick={() => setActiveTab("udp")}
                        className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "udp"
                            ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm"
                            : "text-slate-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-800/50"
                            }`}
                    >
                        <Radio className="w-3.5 h-3.5" />
                        UDP <span className="ml-1 px-1 py-0.5 rounded-md bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[8px] leading-none">{udpData.length}</span>
                    </button>
                </div>
            </div>

            {/* Table Area */}
            <div className="bg-white dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm dark:shadow-xl overflow-hidden">
                <div className="overflow-x-auto custom-scroll">
                    {activeTab === "tcp" ? (
                        <table className="w-full table-fixed border-collapse">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800">
                                    <SortableHeader label="State" sortKey="state" className="w-1/3" />
                                    <SortableHeader label="Local Address" sortKey="local_address" className="w-1/3" />
                                    <SortableHeader label="Remote Address" sortKey="remote_address" className="w-1/3" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-gray-800/60">
                                {paginatedData.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="px-4 py-6 text-center text-slate-500 dark:text-slate-100 text-[10px] font-black uppercase tracking-widest">
                                            No TCP connections found.
                                        </td>
                                    </tr>
                                ) : (
                                    (paginatedData as TcpConnection[]).map((conn, idx) => (
                                        <tr key={`tcp-${idx}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors group">
                                            <td className="px-4 py-1.5 whitespace-nowrap">
                                                <span className={`inline-block text-[8px] font-black uppercase px-1.5 py-0.5 rounded border ${conn.state.toLowerCase() === 'listen' ? 'bg-sky-500/10 text-sky-600 dark:text-sky-500 border-sky-500/20' :
                                                    conn.state.toLowerCase() === 'established' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/20' :
                                                        'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                                                    }`}>
                                                    {conn.state}
                                                </span>
                                            </td>
                                            <td className="px-4 py-1.5 text-center whitespace-nowrap">
                                                <div className="flex items-baseline gap-0.5">
                                                    <span className="text-[11px] text-slate-700 dark:text-slate-300 font-mono font-bold tracking-tight">{conn.local_address}</span>
                                                    <span className="text-[10px] text-sky-600 dark:text-sky-400 font-black">:{conn.local_port}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-1.5 text-center whitespace-nowrap">
                                                <div className="flex items-baseline gap-0.5">
                                                    <span className="text-[11px] text-slate-700 dark:text-slate-300 font-mono font-bold tracking-tight">{conn.remote_address}</span>
                                                    <span className="text-[10px] text-sky-600 dark:text-sky-400 font-black">:{conn.remote_port}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <table className="w-full table-fixed border-collapse">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800">
                                    <SortableHeader label="Local Address" sortKey="local_address" className="w-1/2" />
                                    <SortableHeader label="Port" sortKey="local_port" className="w-1/2" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-gray-800/60">
                                {paginatedData.length === 0 ? (
                                    <tr>
                                        <td colSpan={2} className="px-4 py-6 text-center text-slate-500 dark:text-slate-100 text-[10px] font-black uppercase tracking-widest">
                                            No UDP listeners found.
                                        </td>
                                    </tr>
                                ) : (
                                    (paginatedData as UdpListener[]).map((listener, idx) => (
                                        <tr key={`udp-${idx}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors group">
                                            <td className="px-4 py-1.5 whitespace-nowrap">
                                                <div className="flex items-baseline gap-0.5">
                                                    <span className="text-[11px] text-slate-700 dark:text-slate-300 font-mono font-bold tracking-tight">{listener.local_address}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-1.5 whitespace-nowrap">
                                                <span className="inline-block text-[11px] px-1.5 py-0.5 rounded text-gray-900 dark:text-white font-mono font-bold">
                                                    {listener.local_port}
                                                </span>
                                            </td>

                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer with API Stats & Pagination */}
                {totalItems > 0 && (
                    <div className="bg-slate-50 dark:bg-gray-900/40 px-4 py-1.5 border-t border-slate-200 dark:border-gray-700 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <ItemsPerPageDropdown
                                value={itemsPerPage}
                                onChange={(val) => {
                                    setItemsPerPage(val);
                                    setCurrentPage(1);
                                }}
                            />
                            <span className="text-[10px] text-slate-500 dark:text-gray-500 font-medium whitespace-nowrap">
                                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                            </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="p-1 text-slate-400 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <ArrowUp className="w-3.5 h-3.5 -rotate-90" />
                            </button>
                            <div className="bg-orange-600/10 border border-orange-500/20 text-orange-500 px-2 py-0.5 rounded text-[10px] font-bold leading-none flex items-center justify-center min-w-[20px]">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="p-1 text-slate-400 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <ArrowDown className="w-3.5 h-3.5 -rotate-90" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NetworkConnections;
